import { noteLockSchema, type NoteLock } from "@bedrock/domain";

export const NOTE_PIN_MIN_LENGTH = 6;
export const NOTE_PIN_MAX_LENGTH = 32;
// OWASP's PBKDF2-HMAC-SHA256 recommendation; each guess at a PIN costs this many hashes.
export const NOTE_LOCK_ITERATIONS = 600_000;

const ADDITIONAL_DATA = new TextEncoder().encode("bedrock-note-lock:v1");
const ASSET_ADDITIONAL_DATA = new TextEncoder().encode("bedrock-sealed-asset:v1");
// Files are sealed in chunks so large videos and PDFs never need two full copies in memory.
const ASSET_CHUNK_SIZE = 1024 * 1024;
const GCM_TAG_SIZE = 16;

/** Keeps the derived key in memory so edits can be re-encrypted without asking for the PIN again. */
export interface UnlockedNote {
  key: CryptoKey;
  lock: NoteLock;
  markdown: string;
}

export function notePinProblem(pin: string): string | null {
  if (!/^\d*$/.test(pin)) return "Use numbers only.";
  if (pin.length < NOTE_PIN_MIN_LENGTH) return `Use at least ${NOTE_PIN_MIN_LENGTH} digits.`;
  if (pin.length > NOTE_PIN_MAX_LENGTH) return `Use ${NOTE_PIN_MAX_LENGTH} digits or fewer.`;
  return null;
}

export async function lockNote(pin: string, markdown: string, iterations = NOTE_LOCK_ITERATIONS): Promise<UnlockedNote> {
  if (notePinProblem(pin)) throw new RangeError("The PIN does not meet the lock requirements");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveKey(pin, salt, iterations);
  const lock = await encryptPayload(key, { version: 1, iterations, salt: toBase64(salt) }, { markdown });
  return { key, lock, markdown };
}

/** Re-encrypts an edited body with the key from an earlier unlock and a fresh IV. */
export async function relockNote(session: UnlockedNote, markdown: string): Promise<UnlockedNote> {
  const lock = await encryptPayload(session.key, session.lock, { markdown });
  return { key: session.key, lock, markdown };
}

/** Returns null when the PIN is wrong or the ciphertext was tampered with. */
export async function unlockNote(pin: string, unsafeLock: NoteLock): Promise<UnlockedNote | null> {
  const lock = noteLockSchema.parse(unsafeLock);
  const key = await deriveKey(pin, fromBase64(lock.salt), lock.iterations);
  const body = await decryptPayload(key, lock) as { markdown?: unknown } | null;
  if (typeof body?.markdown !== "string") return null;
  return { key, lock, markdown: body.markdown };
}

/**
 * A locked folder holds a P-256 key pair. Its public key is stored in the clear so anything
 * dropped in can be sealed without the PIN; its private key is encrypted under the PIN.
 */
export interface FolderKey {
  lock: NoteLock;
  privateKey: CryptoKey;
}

/** The key one sealed item is encrypted with, agreed between a one-time key and the folder's. */
export interface ItemKey {
  key: CryptoKey;
  ephemeralKey: string;
  folderLock: NoteLock;
}

export interface SealedAssetHeader {
  nonce: string;
  chunkSize: number;
  size: number;
  type: string;
}

/** What a sealed item keeps inside its ciphertext. */
export interface SealedPayload {
  props: Record<string, unknown>;
  asset?: SealedAssetHeader;
}

const ECDH = { name: "ECDH", namedCurve: "P-256" } as const;
const ITEM_KEY_INFO = new TextEncoder().encode("bedrock-sealed-item:v1");

export async function lockFolder(pin: string, iterations = NOTE_LOCK_ITERATIONS): Promise<FolderKey> {
  if (notePinProblem(pin)) throw new RangeError("The PIN does not meet the lock requirements");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const pinKey = await deriveKey(pin, salt, iterations);
  const pair = await crypto.subtle.generateKey(ECDH, true, ["deriveBits"]);
  const privateKey = toBase64(new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey)));
  const publicKey = toBase64(new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey)));
  const lock = await encryptPayload(pinKey, { version: 1, iterations, salt: toBase64(salt) }, { folder: true, privateKey }, { scope: "folder", publicKey });
  return { lock, privateKey: await importPrivateKey(privateKey) };
}

/** Returns null when the PIN is wrong. */
export async function unlockFolder(pin: string, unsafeLock: NoteLock): Promise<FolderKey | null> {
  const lock = noteLockSchema.parse(unsafeLock);
  const pinKey = await deriveKey(pin, fromBase64(lock.salt), lock.iterations);
  const body = await decryptPayload(pinKey, lock) as { folder?: unknown; privateKey?: unknown } | null;
  if (body?.folder !== true || typeof body.privateKey !== "string") return null;
  return { lock, privateKey: await importPrivateKey(body.privateKey) };
}

/** A fresh key for sealing one item into a folder; needs only the folder's public key. */
export async function createItemKey(folderLock: NoteLock): Promise<ItemKey> {
  if (!folderLock.publicKey) throw new Error("This folder can't take sealed items");
  const folderPublic = await crypto.subtle.importKey("raw", fromBase64(folderLock.publicKey), ECDH, false, []);
  const ephemeral = await crypto.subtle.generateKey(ECDH, true, ["deriveBits"]);
  const key = await agreeItemKey(ephemeral.privateKey, folderPublic, folderLock);
  const ephemeralKey = toBase64(new Uint8Array(await crypto.subtle.exportKey("raw", ephemeral.publicKey)));
  return { key, ephemeralKey, folderLock };
}

/** Recovers a sealed item's key with the folder's private key; null if it belongs elsewhere. */
export async function openItemKey(folder: FolderKey, itemLock: NoteLock): Promise<ItemKey | null> {
  if (!itemLock.ephemeralKey || itemLock.salt !== folder.lock.salt) return null;
  const ephemeralPublic = await crypto.subtle.importKey("raw", fromBase64(itemLock.ephemeralKey), ECDH, false, []);
  const key = await agreeItemKey(folder.privateKey, ephemeralPublic, folder.lock);
  return { key, ephemeralKey: itemLock.ephemeralKey, folderLock: folder.lock };
}

export async function sealPayload(itemKey: ItemKey, payload: SealedPayload): Promise<NoteLock> {
  return encryptPayload(itemKey.key, itemKey.folderLock, payload, { scope: "item", ephemeralKey: itemKey.ephemeralKey });
}

export async function openPayload(itemKey: ItemKey, lock: NoteLock): Promise<SealedPayload | null> {
  const body = await decryptPayload(itemKey.key, lock) as Partial<SealedPayload> | null;
  return body?.props && typeof body.props === "object" ? body as SealedPayload : null;
}

export async function sealAsset(itemKey: ItemKey, blob: Blob): Promise<{ blob: Blob; header: SealedAssetHeader }> {
  const nonce = crypto.getRandomValues(new Uint8Array(8));
  const parts: ArrayBuffer[] = [];
  const chunks = Math.max(1, Math.ceil(blob.size / ASSET_CHUNK_SIZE));
  for (let index = 0; index < chunks; index += 1) {
    const plaintext = await blob.slice(index * ASSET_CHUNK_SIZE, (index + 1) * ASSET_CHUNK_SIZE).arrayBuffer();
    parts.push(await crypto.subtle.encrypt({ name: "AES-GCM", iv: chunkIv(nonce, index), additionalData: ASSET_ADDITIONAL_DATA }, itemKey.key, plaintext));
  }
  return { blob: new Blob(parts), header: { nonce: toBase64(nonce), chunkSize: ASSET_CHUNK_SIZE, size: blob.size, type: blob.type } };
}

/** Rejects when the bytes were altered or sealed with another key. */
export async function openAsset(itemKey: ItemKey, sealed: Blob, header: SealedAssetHeader): Promise<Blob> {
  const nonce = fromBase64(header.nonce);
  const sealedChunkSize = header.chunkSize + GCM_TAG_SIZE;
  const chunks = Math.max(1, Math.ceil(header.size / header.chunkSize));
  if (sealed.size !== header.size + chunks * GCM_TAG_SIZE) throw new Error("The sealed file is incomplete");
  const parts: ArrayBuffer[] = [];
  for (let index = 0; index < chunks; index += 1) {
    const ciphertext = await sealed.slice(index * sealedChunkSize, (index + 1) * sealedChunkSize).arrayBuffer();
    parts.push(await crypto.subtle.decrypt({ name: "AES-GCM", iv: chunkIv(nonce, index), additionalData: ASSET_ADDITIONAL_DATA }, itemKey.key, ciphertext));
  }
  return new Blob(parts, { type: header.type });
}

async function importPrivateKey(pkcs8: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", fromBase64(pkcs8), ECDH, false, ["deriveBits"]);
}

async function agreeItemKey(privateKey: CryptoKey, publicKey: CryptoKey, folderLock: NoteLock): Promise<CryptoKey> {
  const shared = await crypto.subtle.deriveBits({ name: "ECDH", public: publicKey }, privateKey, 256);
  const material = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt: fromBase64(folderLock.salt), info: ITEM_KEY_INFO }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

function chunkIv(nonce: Uint8Array, index: number): Uint8Array<ArrayBuffer> {
  const iv = new Uint8Array(12);
  iv.set(nonce);
  new DataView(iv.buffer).setUint32(8, index);
  return iv;
}

async function deriveKey(pin: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt, iterations }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

async function encryptPayload(key: CryptoKey, header: Pick<NoteLock, "version" | "iterations" | "salt">, payload: unknown, extra: Pick<NoteLock, "scope" | "publicKey" | "ephemeralKey"> = {}): Promise<NoteLock> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: ADDITIONAL_DATA }, key, plaintext);
  return noteLockSchema.parse({ version: header.version, iterations: header.iterations, salt: header.salt, iv: toBase64(iv), ciphertext: toBase64(new Uint8Array(ciphertext)), ...extra });
}

async function decryptPayload(key: CryptoKey, lock: NoteLock): Promise<unknown> {
  try {
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromBase64(lock.iv), additionalData: ADDITIONAL_DATA }, key, fromBase64(lock.ciphertext));
    return JSON.parse(new TextDecoder().decode(plaintext)) as unknown;
  } catch {
    return null;
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}
